import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * Establish a WebSocket connection for a task's chat room, using a short-lived
 * server-issued ticket (auth tokens never leave httpOnly cookies in JS land).
 *
 * Calls `onMessage(payload)` for each incoming JSON message.
 */
export default function useTaskWebSocket({ taskId, onMessage, enabled = true }) {
  const wsRef = useRef(null);

  useEffect(() => {
    if (!enabled || !taskId) return undefined;
    let ws = null;
    let cancelled = false;

    const open = async () => {
      let ticket;
      try {
        const { data } = await api.post("/auth/ws-ticket");
        ticket = data.ticket;
      } catch (err) {
        console.error("useTaskWebSocket: ws-ticket request failed", err);
        return;
      }
      if (cancelled || !ticket) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = backendUrl.replace(/^http/, "ws");
      try {
        ws = new WebSocket(`${wsBase}/api/ws/tasks/${taskId}?token=${encodeURIComponent(ticket)}`);
      } catch (err) {
        console.error("useTaskWebSocket: WebSocket construction failed", err);
        return;
      }
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          onMessage?.(payload);
        } catch (err) {
          console.warn("useTaskWebSocket: dropped malformed payload", err);
        }
      };
      ws.onerror = () => {
        // socket errors are surfaced through onclose; nothing actionable here
      };
    };

    open();

    return () => {
      cancelled = true;
      const current = wsRef.current;
      if (current) {
        try {
          current.close();
        } catch {
          // already closed
        }
      }
      wsRef.current = null;
    };
  }, [taskId, enabled, onMessage]);

  return wsRef;
}
